import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryResult,
  type UseMutationResult,
} from '@tanstack/react-query';
import { queryKeys, type UnitLocation } from '@vtak/shared';
import { workspaceClient, type NewLocationInput } from '@/services/workspace-client';

/** Load the visible locations for a unit (own + inherited). */
export function useLocationsQuery(unitId: string | null): UseQueryResult<UnitLocation[]> {
  return useQuery({
    queryKey: queryKeys.workspace.locations(unitId ?? ''),
    queryFn: () => workspaceClient.getLocations(unitId ?? ''),
    enabled: Boolean(unitId),
  });
}

/** Create a location, invalidating the unit's location list on success. */
export function useCreateLocation(
  unitId: string,
): UseMutationResult<UnitLocation, Error, NewLocationInput> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: NewLocationInput) => workspaceClient.createLocation(unitId, input),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.workspace.locations(unitId) });
    },
  });
}

/** Delete a location, invalidating the unit's location list on success. */
export function useDeleteLocation(unitId: string): UseMutationResult<{ ok: boolean }, Error, string> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => workspaceClient.deleteLocation(unitId, id),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.workspace.locations(unitId) });
    },
  });
}
